import { ConnectError } from '@connectrpc/connect';
import { ErrDetailSchema } from './gen/common/protos/types_pb';
import { DescMessage, MessageInitShape } from '@bufbuild/protobuf';

const knownErrors = new Set([
  // login
  'LoginIncorrect',
  'LoginTooManyAttempts',
  'Login2FARequired',
  'LoginConfirmIdentityRequired',
  'LoginConfirmIPAddrRequired',
  'LoginRefreshFromUnknownIP',
  'LoginAllowIPAddrRequired',
  'InvalidActivationCode',
  'ExpiredActivationCode',
  'LoginInvalidPin',
  'LoginInvalidPinAlreadyUsed',

  // JWT
  'MissingAuthToken',
  'InvalidAuthToken',
  'RefreshTokenFailed',
  'ExpiredSession',

  // SSO
  'MissingSSOMetadata',
  'MissingSSOEntityDescriptor',
  'MissingSSOEntityId',
  'MissingSSOCertificate',
  'PermissionDenied',
  'RemovingSSOWasUnsuccessful',
  'InvalidXML',
  'FetchingMetadataFailed',
  'UserNotApproved',
] as const);

type KnownError = typeof knownErrors extends Set<infer T> ? T : never;

type TypedArray =
  | Int8Array
  | Uint8Array
  | Uint8ClampedArray
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Float32Array
  | Float64Array
  | BigInt64Array
  | BigUint64Array;

export type Data<T> = (T extends DescMessage ? MessageInitShape<T> : T) extends infer O
  ? O extends Array<infer E>
    ? Array<Data<E>>
    : O extends ReadonlyArray<infer E>
      ? ReadonlyArray<Data<E>>
      : O extends TypedArray
        ? O
        : O extends object
          ? Prettify<Omit<{ [K in keyof O]: Data<O[K]> }, '$unknown' | '$typeName'>>
          : O
  : never;
export type PartialMessage<T> =
  T extends Array<infer E>
    ? Array<PartialMessage<E>>
    : T extends ReadonlyArray<infer E>
      ? ReadonlyArray<PartialMessage<E>>
      : T extends TypedArray
        ? T
        : T extends object
          ? Data<T> extends infer D
            ? Prettify<{
                [K in keyof D]?: PartialMessage<D[K]>;
              }>
            : never
          : T;

export type ConnectResult<T, E extends `rest.${KnownError}` = `rest.${KnownError}`> =
  | {
      readonly success: false;
      readonly error: E;
      readonly details?: undefined;
    }
  | {
      readonly success: false;
      readonly error: 'rest.ReportToEngineering';
      readonly details: { uuid: string };
    }
  | {
      readonly success: true;
      readonly data: Data<T>;
    };

export async function getConnectResult<T>(action: () => Promise<T>): Promise<ConnectResult<T>> {
  try {
    const data = (await action()) as Data<T>;
    return { success: true, data };
  } catch (e: unknown) {
    // we can extract some details from a ConnectError
    if (e instanceof ConnectError) {
      // if we can find details inside the error then we try to extract a (known) error from that
      const details = e.findDetails(ErrDetailSchema);
      if (details.length > 0) {
        const error = details[0].reason as KnownError;
        if (knownErrors.has(error)) {
          return { success: false, error: `rest.${error}` };
        }
      } else {
        // otherwise we just use our standard error message
        // and push the message into the uuid field to make it part of the error message the user sees
        return {
          success: false,
          error: `rest.ReportToEngineering`,
          details: { uuid: e.message },
        };
      }
    }

    // otherwise we just use our standard error message
    // and try our best to turn the error into a message,
    // and push that into the uuid field to make it part of the error message the user sees
    return {
      success: false,
      error: 'rest.ReportToEngineering',
      details: { uuid: e instanceof Error ? e.name : typeof e === 'string' ? e : 'unknown' },
    };
  }
}
